import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useModuleStore } from '@/store/module'
import { useAppStore } from '@/store/app'
import authRoutes from './modules/auth'
import { generateModuleRoutes } from '@/router/helpers/generateModuleRoutes'
import { useUserStore } from '@/store/user'
import { useAuthStore } from '@/store/authentication'
import { getUserInfo } from '@/api/requests/user/login'

const routes: RouteRecordRaw[] = [
    {
        path: '/',
        name: 'dashboard',
        component: () => import('@/pages/DashboardPage.vue'),
        meta: {
            layout: 'DefaultLayout',
            module: null,
        },
    },
    {
        path: '/profile',
        name: 'profile',
        component: () => import('@/pages/ProfileSettings.vue'),
        meta: {
            layout: 'DefaultLayout',
            module: null,
        },
    },
    {
        path: '/logout',
        name: 'logout',
        component: () => import('@/pages/LogoutPage.vue'),
        meta: {
            layout: 'SimpleLayout',
            module: null,
        },
    },
    ...authRoutes,
    {
        path: '/c-application-logs',
        name: 'c-application-logs',
        component: () => import('@/pages/ClickhouseLogsPage.vue'),
        meta: {
            layout: 'DefaultLayout',
            module: 'c-application-logs',
        },
    },
    ...generateModuleRoutes,
    {
        path: '/settings',
        name: 'settings',
        component: () => import('@/pages/Settings/SettingsNew.vue'),
        meta: {
            layout: 'DefaultLayout',
            module: 'settings',
        },
    },

]

const router = createRouter({
    history: createWebHistory(),
    routes,
})

router.beforeEach(async (to, from) => {
    const userStore = useUserStore()
    const authStore = useAuthStore()

    // Necessary to set route meta.module so route can be recognized
    to.meta.module = to.params.module

    if (to.meta.module !== from.meta.module) {
        if (from.meta.module && to.name !== 'profile') {
            const appStore = useAppStore()

            appStore.abortController?.abort()
            appStore.abortController = undefined
        }
    }

    if (!userStore.user && to.name !== 'login') {
        try {
            await getUserInfo()
        } catch {
        }

        if (!userStore.user && !authStore.isAuthenticated()) {
            return {
                name: 'login',
                query: {
                    intended: to.fullPath,
                },
            }
        }
    }
})

router.afterEach((to) => {
    const moduleStore = useModuleStore()
    const appStore = useAppStore()
    const userStore = useUserStore()
    const module = to.meta.module as string

    appStore.resetRevealedPhoneNumbers()

    if (module && userStore.user) {
        if (! moduleStore.module || moduleStore.module.name !== module) {
            moduleStore.describeModule(module)
            moduleStore.editEnabled = false
        }
    }
})

// After a deploy, an already-open tab may reference chunks of the previous build that no longer exist.
// Reload once to pick up the new index.html; the timestamp guard prevents a reload loop.
const CHUNK_RELOAD_KEY = 'chunk-reload-at'

const reloadOnStaleChunk = (path?: string): boolean => {
    try {
        const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0)

        if (Date.now() - lastReload < 10_000) {
            return false
        }

        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
    } catch {
    }

    path ? window.location.assign(path) : window.location.reload()

    return true
}

const isChunkLoadError = (error: unknown): boolean =>
    error instanceof Error
    && /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(error.message)

router.onError((error, to) => {
    if (isChunkLoadError(error)) {
        reloadOnStaleChunk(to.fullPath)
    }
})

window.addEventListener('vite:preloadError', (event) => {
    if (reloadOnStaleChunk()) {
        event.preventDefault()
    }
})

export default router
